import { Loader2, LogIn, Monitor, MonitorPlay, MonitorX } from 'lucide-react'
import { toast } from 'sonner'

import { useCDPStatus, useStartCDPLaunch, type CDPLaunchVars } from '@/api/endpoints/scraping'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'

import styles from './CDPConnectButton.module.scss'
import { errorMessage } from '@/lib/errors'

/**
 * Status + launch trigger for the managed Chromium that the CDP scraper
 * drives. Sits above the URL input in the product-review form because
 * scraping a Shopee URL silently fails if the browser isn't up — the
 * user needs to see that and have the affordance to fix it in one click.
 *
 * Two action affordances:
 *
 * - "Conectar navegador" — uses the backend default (``CDP_HEADLESS``,
 *   typically headless = no window pops up). For routine scrapes after
 *   the cookies have been seeded.
 * - "Abrir janela pra login" — forces ``headless=false`` and ``force=true``
 *   so the operator gets a visible Chromium window to log into Shopee /
 *   Amazon or solve a captcha. ``force`` kills any current Chromium so
 *   the headless → headed swap actually takes effect.
 *
 * Connected state hides the primary button (no need to spawn again) but
 * keeps the "Abrir janela" affordance visible — login goes stale every
 * few weeks and that button is the recovery path.
 */
export function CDPConnectButton() {
  const status = useCDPStatus()
  const launch = useStartCDPLaunch()

  const handleLaunch = (vars: CDPLaunchVars, successMsg: string) => {
    launch.mutate(vars, {
      onSuccess: (data) => {
        if (data.launched_now) {
          toast.success(successMsg, {
            description: vars.headless === false
              ? 'A janela do Chromium abriu no seu desktop. Faça login no Shopee/Amazon — os cookies ficam salvos pra próxima vez.'
              : 'Spawn em modo oculto. Próxima geração já usa esse navegador.',
            duration: 8_000,
          })
        } else {
          toast.success('Navegador já está conectado', {
            description: data.browser ?? undefined,
          })
        }
      },
      onError: (error) => {
        toast.error('Não consegui abrir o navegador', {
          description: errorMessage(error, 'Falha ao abrir o Chrome'),
          duration: 10_000,
        })
      },
    })
  }

  // Loading state — show the row but render a spinner instead of the
  // status chip so the layout doesn't jump when data lands.
  if (status.isLoading) {
    return (
      <div className={styles.shell}>
        <span className={styles.icon}>
          <Loader2 size={16} className={styles.spin} />
        </span>
        <div className={styles.body}>
          <div className={styles.title}>Verificando navegador…</div>
        </div>
      </div>
    )
  }

  const data = status.data
  const available = data?.available ?? false
  const launching = launch.isPending

  return (
    <div className={styles.shell}>
      <span className={styles.icon}>
        {available ? <MonitorPlay size={16} /> : <MonitorX size={16} />}
      </span>

      <div className={styles.body}>
        <div className={styles.title}>
          Navegador para scraping
          {available ? (
            <Chip variant="success">Conectado</Chip>
          ) : (
            <Chip variant="warning">Desconectado</Chip>
          )}
        </div>
        {available && data?.browser ? (
          <div className={styles.detail}>{data.browser}</div>
        ) : null}
        {!available && launch.isError ? (
          // Surface the backend's 503 message only after a launch attempt
          // failed — that's an actionable error the user should react to
          // (most often a missing DISPLAY/WSLg mount or spawn timeout).
          // The polling-time "Cannot reach localhost:9222 ConnectError"
          // is just "Chromium isn't running yet" in jargon and would be
          // noise on every page load before the user clicks the button.
          <div className={styles.error}>
            {errorMessage(launch.error, 'Falha ao abrir o Chrome')}
          </div>
        ) : null}
      </div>

      <div className={styles.actions}>
        {!available ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              handleLaunch({}, 'Navegador aberto')
            }}
            disabled={launching}
            leadingIcon={launching ? <Loader2 size={14} className={styles.spin} /> : <Monitor size={14} />}
          >
            {launching ? 'Abrindo…' : 'Conectar navegador'}
          </Button>
        ) : null}
        {/* "Abrir janela pra login" — always visible, including when
            connected, because login periodically expires and this is the
            recovery path. force=true kills the current Chromium so the
            headless → headed mode swap takes effect. */}
        <Button
          variant={available ? 'ghost' : 'secondary'}
          size="sm"
          onClick={() => {
            handleLaunch({ force: true, headless: false }, 'Janela aberta pra login')
          }}
          disabled={launching}
          leadingIcon={launching ? <Loader2 size={14} className={styles.spin} /> : <LogIn size={14} />}
        >
          {launching ? 'Abrindo…' : available ? 'Abrir janela pra login' : 'Abrir janela'}
        </Button>
      </div>
    </div>
  )
}
